import router from '@/router'
import { v4 as uuid } from 'uuid'
import { getHandCorrectness, updateHsSpotActions } from '@/helpers/trainer'
import {
  getHistoryUrlParams,
  unsetHsSelectedAction,
  removePracticeActionId,
  setHsBot,
} from '@/store/gamestate/gamestate'

// global vars keyed by namespace
const newHandTimeoutId = {}
const quickResultTimeoutId = {}
const timebanksResolved = {}

const getInitialState = ({ namespace }) => ({
  trainer_namespace: namespace,
  trainer_hand_uid: uuid(), // gets assigned on new hand state
  trainer_practice_hand_id: null, // gets assigned after calling setup
  trainer_api_game_properties: null,

  trainer_custom_tree: null,

  trainer_hand_settings: '',
  trainer_setup_repeat_params: null,
  trainer_error: null,
  trainer_hand_finished: false,

  // competitive tracking
  trainer_changed_move: false,
  trainer_cheats_registered: false,

  // for TrainerResultTile
  trainer_quick_result: null,
  trainer_quick_result_visible: false,

  // show actions in result-state
  trainer_result_visible: false,

  trainer_new_hand_timeout: 0,
  trainer_slider_actions: null,

  trainer_timebank: null,
  trainer_timebank_timeout: 0,

  trainer_overview_spot: null, // where info panel gets its data
  trainer_instahand_gg_visible: true, // keep gg-screen visible in replayer

  // trainer_quizz_visible: false,
  trainer_quizz_visible: true,
  trainer_quizz_type: 'action_frequency', // 'action_frequency', 'eq', 'solution'
})

const getters = {
  trainer_quizz_visible: state => state.trainer_quizz_visible,
  trainer_quizz_type: state => state.trainer_quizz_type,

  trainer_custom_tree: state => state.trainer_custom_tree,

  trainer_instahand_gg_visible: state => state.trainer_instahand_gg_visible,
  trainer_state_props: state => {
    const { trainer_namespace: _trainer_namespace, ...rest } = state
    return rest
  },
  trainer_hand_uid: state => state.trainer_hand_uid,
  trainer_practice_hand_id: state => state.trainer_practice_hand_id,
  trainer_setup_repeat_params: state => state.trainer_setup_repeat_params,
  trainer_overview_spot: (state, getters) => state.trainer_overview_spot,
  trainer_changed_move: state => state.trainer_changed_move,
  trainer_new_hand_timeout: state => state.trainer_new_hand_timeout,
  trainer_hand_settings: state => state.trainer_hand_settings,
  trainer_hand_finished: state => state.trainer_hand_finished,
  trainer_result_visible: state => state.trainer_result_visible,
  trainer_timebank: state => state.trainer_timebank,
  trainer_timebank_timeout: state => state.trainer_timebank_timeout,
  trainer_quick_result: state => state.trainer_quick_result,
  trainer_slider_actions: state => state.trainer_slider_actions,
  trainer_history_hand: (state, getters, rootState, rootGetters) => {
    let res = rootGetters.history_hands.find(i => i.uid === state.trainer_hand_uid)
    res = res ? JSON.parse(res?.stringified_hand) : null
    return res
  },
  trainer_table_locked: (state, getters, rootState, rootGetters) => {
    const res = rootGetters.locked_namespaces.some(nn => nn === state.trainer_namespace)
    return res
  },
  trainer_info_visible: (state, getters) => {
    if (state.trainer_hand_finished) return true
    if (getters.query_fullhand_setup.fh_trainer_learning_mode) {
      return !!(getters.trainer_result_visible && getters.hero_spot)
    }
    return true
  },
  trainer_anim_delay: (state, getters) => {
    const speed = getters.query_fullhand_setup.fh_trainer_game_speed
    const res = speed === 'normal' ? 1700 : speed === 'fast' ? 600 : 300
    return res
  },

  // trainer register cheats
  trainer_should_register_cheats: (state, getters) =>
    !getters.trainer_cheats_registered && !getters.trainer_is_competitive,
  trainer_cheats_registered: state => state.trainer_cheats_registered,
  trainer_is_competitive: (state, getters) => {
    const { trainer_changed_move, query_fullhand_setup } = getters
    // did not change move, frequency hint off, quicks results off
    const res =
      !query_fullhand_setup.fh_trainer_hero_frequency &&
      !query_fullhand_setup.fh_trainer_quick_result &&
      !trainer_changed_move
    return res
  },
  trainer_get_hand_settings:
    (state, getters, rootState, rootGetters) =>
    ({ settingsHand = null, resetTmp = true } = {}) => {
      let l_sessionSettings = null

      if (!settingsHand) {
        let history_spots = []
        let fhQuery = {
          ...getters.query_fullhand_preferences,
          ...getters.query_fullhand_game,
          ...getters.query_board_filter,
          ...getters.query_gameformat,
          ...getters.query_cussol,
          board: getters.query_board,
          board_tmp: getters.query_board_tmp,
          hand_tmp: getters.query_hand_tmp,
        }
        if (fhQuery.fh_start_spot === 'custom_spot') {
          // RF_TODO_11 - pass this to afunction returnig hspots and query
          history_spots = getters.history_spots
          const hSpot = history_spots.find(i => i.is_active)
          const historyParams = getHistoryUrlParams({ history_spots, includeShowdown: false })
          history_spots = history_spots.filter(e => e.id <= hSpot?.id)
          history_spots = unsetHsSelectedAction({ history_spots, spot: hSpot })
          fhQuery = {
            ...historyParams,
            history_spot: hSpot?.id,
            fh_hero: hSpot?.position,
            fh_opponent: '',
          }
        }
        l_sessionSettings = {
          history_spots,
          query: fhQuery,
        }
      }

      const trainer_hand_settings = settingsHand || l_sessionSettings
      trainer_hand_settings.query.fh_trainer_tables = rootGetters['tra/alpha/fh_trainer_tables']

      if (resetTmp) {
        trainer_hand_settings.query = {
          ...trainer_hand_settings.query,
          board_tmp: '',
          hand_tmp: '',
          hand_op_tmp: '',
          fh_hero_tmp: '',
          fh_opponent_tmp: '',
          fh_action_tmp: '',
        }
      }

      return trainer_hand_settings
    },
}

const actions = {
  trainer_router_update_query({ state, commit, dispatch, getters, rootGetters }, { push, query }) {
    const routeNameMatch = router.currentRoute.value.name === 'PracticeTrainer'
    const canPush = routeNameMatch ? push : false
    const canReplace = routeNameMatch && state.trainer_namespace === rootGetters.active_namespace
    dispatch(`router_update_query`, {
      query,
      push: canPush,
      replace: canReplace,
      saveLocally: false,
    })
  },
  trainer_gamestate_set_history(
    { state, commit, dispatch, getters, rootGetters },
    { history_spots, spot_id, query = {}, push },
  ) {
    const routeNameMatch = router.currentRoute.value.name === 'PracticeTrainer'
    const canPush = routeNameMatch ? push : false
    const canReplace = routeNameMatch && state.trainer_namespace === rootGetters.active_namespace
    dispatch('gamestate_set_history', {
      history_spots,
      spot_id,
      query,
      push: canPush,
      replace: canReplace,
      saveLocally: false,
    })
  },
  trainer_reset({ state, commit, dispatch, getters }, { initialState = {} } = {}) {
    commit('trainer_reset', initialState)
  },
  trainer_history_push_state({ state, commit, dispatch, getters, rootGetters }) {
    const stats_hand = rootGetters[`stats_hand_${state.trainer_namespace}`.replace('tra/', '')]
    const { drills_selected_drill, session_uid } = rootGetters
    const { history_spots, showdown_spot, query_trainer_history } = getters

    const { trainer_namespace: _trainer_namespace, ...trainer_state } = state

    const trainerState = {
      ...trainer_state,
      trainer_error: null,
      trainer_overview_spot: null,
      trainer_quick_result: null,
      trainer_slider_actions: null,
      trainer_timebank: null,
    }

    let correctness = null
    const {fh_trainer_mode} = query_trainer_history
    const stopAfterAction = fh_trainer_mode === 'stop_after_action'
    const hero_position = query_trainer_history.fh_hero_tmp

    if (stopAfterAction && !showdown_spot) {
      const { available_actions } = getters
      const selected_action = available_actions.find(i => i.is_preselected)
      if (selected_action.correctness === 'na') {
        console.log('NA hand - ommited in history')
        return
      }
      const handCorrectness = getHandCorrectness({
        history_spots,
        hero_position,
      })
      correctness = {
        ...handCorrectness,
        total_ev_loss: selected_action.ev_loss,
        correctness_group: selected_action.correctness_group,
      }
    } else {
      correctness = getHandCorrectness({ history_spots, hero_position })
      dispatch('streak_set', { correctness_group: correctness.correctness_group }, { root: true })
    }

    const hand = {
      uid: state.trainer_hand_uid,
      marked: false,
      session_uid,
      namespace: state.trainer_namespace,
      history_spots,
      street: history_spots[history_spots.length - 1].street,
      correctness,
      query_trainer_history,
      trainer_state: trainerState,
      drills_selected_drill,
      stats_hand,
      created_at: Date.now() - 1, // so session item comes first when ordered in history
    }
    dispatch('history_add_hand', { hand }, { root: true })
    dispatch('stats_add_hand', { namespace: state.trainer_namespace }, { root: true })
    dispatch('session_add_hand', { hand_uid: state.trainer_hand_uid }, { root: true })
  },
  trainer_register_cheats({ state, commit, dispatch, getters }) {
    if (getters.trainer_should_register_cheats) {
      dispatch(
        'api_practice_register_cheats',
        { namespace: state.trainer_namespace },
        { root: true },
      )
    }
  },
  trainer_set_cheats_reqistered({ state, commit, dispatch, getters }, { value = true }) {
    commit('trainer_set', { key: 'trainer_cheats_registered', value })
  },

  // --------------------------------------------------
  trainer_new_hand({ state, commit, dispatch, getters, rootGetters }) {
    if (!state.trainer_hand_finished) {
      dispatch('session_unregister_hand', { hand_uid: state.trainer_hand_uid }, { root: true })
    }
    dispatch('session_start', null, { root: true })

    const { trainer_hand_settings, query_fullhand_preferences } = getters
    const lastSettings = trainer_hand_settings
      ? JSON.parse(trainer_hand_settings)
      : getters.trainer_get_hand_settings({ resetTmp: true })
    const settingsHand = {
      ...lastSettings,
      query: {
        ...lastSettings.query,
        ...query_fullhand_preferences,
      },
    }
    dispatch('trainer_hand_initialize', { settingsHand })
  },
  trainer_repeat_hand({ state, commit, dispatch, getters }) {
    if (!state.trainer_hand_finished) {
      dispatch('session_unregister_hand', { hand_uid: state.trainer_hand_uid }, { root: true })
    }
    dispatch('session_start', null, { root: true })

    const { trainer_hand_settings, query_fullhand_preferences } = getters
    const lastSettings = trainer_hand_settings
      ? JSON.parse(trainer_hand_settings)
      : getters.trainer_get_hand_settings({ resetTmp: false })

    const settingsHand = {
      ...lastSettings,
      query: {
        ...lastSettings.query,
        ...query_fullhand_preferences,
      },
    }
    dispatch('trainer_hand_initialize', {
      settingsHand,
      repeatHand: true,
    })
  },
  trainer_switch_to_custom_spot({ state, commit, dispatch, getters }) {
    commit(`trainer_set`, { key: 'trainer_result_visible', value: false })

    const { query_board_tmp, history_spots, history_spot, trainer_hand_settings } = getters

    let hSpots = history_spots.filter(i => i.id <= history_spot.id)
    hSpots = unsetHsSelectedAction({ history_spots: hSpots, spot: history_spot })
    hSpots = setHsBot({ history_spots: hSpots, hero_position: history_spot.position })
    const historyQuery = getHistoryUrlParams({ history_spots: hSpots })

    const lastSettings = trainer_hand_settings
      ? JSON.parse(trainer_hand_settings)
      : getters.trainer_get_hand_settings({ settingsHand: null, resetTmp: true })

    const {gameformat} = getters
    const gameformatQuery2 = {
      stacks: gameformat.stacks,
      gametype: gameformat.gametype,
      depth: gameformat.depth,
    }

    const fullSetupQuery = {
      ...lastSettings.query,
      ...historyQuery,
      ...gameformatQuery2,
      history_spot: history_spot.id,
      fh_actions: '',
      fh_start_spot: 'custom_spot',
      fh_hero: history_spot.position,
      fh_hero_tmp: history_spot.position,
      fh_opponent: '',
      fh_opponent_tmp: '',
      board_tmp: query_board_tmp,
      hand_tmp: '',
      hand_op_tmp: '',
    }
    const settingsHand = {
      ...lastSettings,
      history_spots: hSpots,
      query: fullSetupQuery,
    }
    dispatch(`trainer_hand_initialize`, {
      settingsHand,
      keepBoard: true,
      resetTmp: false,
    })
  },
  trainer_hand_return_to_game({ state, commit, dispatch, getters }) {
    // Behaves more like 'play from here' with new hand but keeping original hand settings
    // so hitting new-hand after finishing this hand will trigger orig. start spot
    const {history_spot} = getters
    let hSpots = getters.history_spots.filter(i => i.id <= history_spot.id)
    hSpots = unsetHsSelectedAction({ history_spots: hSpots, spot: history_spot })
    hSpots = removePracticeActionId({ history_spots: hSpots })
    const historyQuery = getHistoryUrlParams({ history_spots: hSpots })

    const {query_query} = getters
    const query = {
      ...query_query,
      ...historyQuery,
      hand_op_tmp: '',
    }

    const { trainer_hand_settings, query_fullhand_preferences } = getters
    const lastSettings = trainer_hand_settings
      ? JSON.parse(trainer_hand_settings)
      : getters.trainer_get_hand_settings({ resetTmp: false })

    const originalSettingsHand = {
      ...lastSettings,
      query: {
        ...lastSettings.query,
        ...query_fullhand_preferences,
      },
    }

    const playFromHere = {
      query,
      hSpots,
      originalSettingsHand,
    }
    dispatch(`trainer_hand_initialize`, {
      settingsHand: null, // get what currently is
      keepBoard: true,
      repeatHand: true,
      playFromHere,
      resetTmp: false,
    })
  },
  // ----------------------------------------

  trainer_hand_finish(
    { state, commit, dispatch, getters, rootGetters },
    { continueNextHand = true } = {},
  ) {
    dispatch('trainer_history_push_state')
    // const { query_fullhand_setup, trainer_live, showdown_spot } = getters
    const { query_fullhand_setup, showdown_spot } = getters
    const qqfh = query_fullhand_setup
    // if (q.fh_trainer_mode === 'stop_after_action' && trainer_live && !showdown_spot) {
    if (qqfh.fh_trainer_mode === 'stop_after_action' && !showdown_spot) {
      commit('trainer_set', { key: 'trainer_result_visible', value: true })
      commit('trainer_set', { key: 'trainer_hand_finished', value: true })
    } else {
      commit('trainer_set', { key: 'trainer_result_visible', value: true })
      commit('trainer_set', { key: 'trainer_hand_finished', value: true })
    }

    const autoplay = getters.router_get('fh_trainer_autoplay')
    // user can leave to diff page and game continues in background

    if (
      !continueNextHand ||
      !autoplay ||
      router.currentRoute.value.name !== 'PracticeTrainer' ||
      rootGetters.stats_session_result_visible ||
      rootGetters.panel_replay
    ) {
      return
    }
    dispatch('trainer_auto_new_hand', { timeout_str: autoplay })
  },
  trainer_hand_initialize(
    { state, commit, dispatch, getters, rootGetters },
    {
      settingsHand = null,
      keepBoard = false,
      repeatHand = false,
      playFromHere = false,
      resetTmp = true,
      unlock = false,
      fh_trainer_tables = null,
    } = {},
  ) {
    // prepare for new hand
    const namespace = state.trainer_namespace
    const trainer_hand_settings = getters.trainer_get_hand_settings({ settingsHand, resetTmp })
    commit('trainer_set', {
      key: 'trainer_hand_settings',
      value: JSON.stringify(trainer_hand_settings),
    })
    dispatch(`trainer_reset_hand`)
    dispatch('trainer_set_new_hand_state')

    // force user to finish remaining hands - diss autoplay
    const numOfTables = fh_trainer_tables || getters.fh_trainer_tables
    // console.log(numOfTables, 'numOfTables', state.trainer_namespace)
    if (numOfTables > 1) {
      const sessionRemainingHands = rootGetters.session_remaining_hands
      // console.log(numOfTables, sessionRemainingHands)
      if (!sessionRemainingHands) {
        dispatch('hands_lock_namespace', { namespace: state.trainer_namespace }, { root: true })
        console.log('locked-table', state.trainer_namespace)
        return
      }
    }
    if (unlock) {
      // console.log('unlocked-table', state.trainer_namespace)
      dispatch('hands_unlock_namespace', { namespace: state.trainer_namespace }, { root: true })
    }

    if (!rootGetters.session_uid) {
      // call for sesion id
      dispatch('hands_call_session', { repeatHand, playFromHere, settingsHand }, { root: true })
      return
    }

    dispatch('session_register_hand', { hand_uid: state.trainer_hand_uid }, { root: true })
    dispatch('trainer_hand_start', {
      settings: trainer_hand_settings,
      repeatHand,
      playFromHere,
      keepBoard,
      namespace,
    })
  },
  trainer_reset_hand({ state, commit, dispatch, getters }) {
    dispatch(`anim_reset`)
    dispatch('trainer_clear_timebank')
    dispatch('trainer_auto_new_hand_stop')
    dispatch('stats_hand_reset', { namespace: state.trainer_namespace }, { root: true })
  },
  trainer_set_new_hand_state({ state, commit, dispatch, getters }) {
    const newState = {
      trainer_hand_uid: uuid(),
      trainer_practice_hand_id: null,
      trainer_custom_tree: null,
      trainer_api_game_properties: null,
      trainer_cheats_registered: false,
      trainer_new_hand_timeout: 0,
      trainer_timebank: null,
      trainer_slider_actions: null,
      trainer_result_visible: false,
      trainer_changed_move: false,
      trainer_error: null,
      trainer_hand_finished: false,
      trainer_overview_spot: null,
      trainer_panel_drills: false,
      trainer_instahand_gg_visible: true,
    }
    commit('trainer_reset', newState)
  },
  trainer_hand_start(
    { state, commit, dispatch, getters },
    { settings = null, keepBoard = false, repeatHand = false, playFromHere = false, namespace },
  ) {
    const { query_cards } = getters
    const q = getters.query_fullhand_setup
    const last_hand_query = {
      fh_hero_tmp: q.fh_hero_tmp,
      fh_opponent_tmp: q.fh_opponent_tmp,
      hand_tmp: query_cards.hand_tmp,
      board_tmp: query_cards.board_tmp,
    }

    const { query, history_spots } = settings

    commit(`gamestate_set`, {
      key: 'history_spots',
      value: history_spots,
    })
    dispatch(`trainer_router_update_query`, {
      query,
      push: false,
    })
    dispatch('gamestate_reset_next')
    // commit('trainer_set', { key: 'trainer_live', value: false })
    // commit('trainer_set', { key: 'trainer_apply_setup', value: true })

    const shouldRegisterCheats = getters.trainer_should_register_cheats

    const repeatParams = state.trainer_setup_repeat_params
    if (repeatHand && repeatParams) {
      dispatch(
        'api_practice_setup_repeat',
        {
          namespace,
          shouldRegisterCheats,
          last_hand_query,
          repeatParams,
          playFromHere,
        },
        { root: true },
      )
    } else {
      dispatch(
        'api_practice_setup',
        {
          namespace,
          shouldRegisterCheats,
          last_hand_query,
          keepBoard,
        },
        { root: true },
      )
    }
  },

  trainer_auto_new_hand(
    { state, commit, dispatch, getters, rootGetters },
    { timeout_str = '3_sec' },
  ) {
    if (!rootGetters.session_finished) {
      dispatch('trainer_auto_new_hand_stop')
      let seconds = Number(timeout_str.substring(0, timeout_str.indexOf('_')))
      if (seconds === 0) {
        const qqfh = getters.query_fullhand_setup
        const stopAfterAction = qqfh.fh_trainer_mode === 'stop_after_action'
        if (stopAfterAction) {
          dispatch('trainer_new_hand')
          return
        }

        // opponent cards flash
        seconds = 1.2
        state.trainer_new_hand_timeout = seconds
        // so setting instahand does not hide ggscreen on finished hand
        commit('trainer_set', { key: 'trainer_instahand_gg_visible', value: false })
        newHandTimeoutId[state.trainer_namespace] = setTimeout(() => {
          dispatch('trainer_new_hand')
        }, seconds * 1000)
        return
      }
      state.trainer_new_hand_timeout = seconds
      // commit('trainer_set', { key: 'trainer_new_hand_timeout', value: seconds })
      newHandTimeoutId[state.trainer_namespace] = setTimeout(() => {
        dispatch('trainer_new_hand')
      }, seconds * 1000 - 50)
    }
  },
  trainer_auto_new_hand_stop({ state, commit, dispatch, getters }) {
    state.trainer_new_hand_timeout = 0
    clearTimeout(newHandTimeoutId[state.trainer_namespace])
  },
  trainer_show_quick_result({ state, commit, dispatch, getters }, { action }) {
    if (getters.query_fullhand_setup.fh_trainer_quick_result) {
      clearTimeout(quickResultTimeoutId[state.trainer_namespace])
      commit('trainer_set', { key: 'trainer_quick_result', value: action })
      commit('trainer_set', { key: 'trainer_quick_result_visible', value: true })
      quickResultTimeoutId[state.trainer_namespace] = setTimeout(() => {
        commit('trainer_set', { key: 'trainer_quick_result_visible', value: false })
      }, 2000)
    }
  },
  // ----------
  trainer_set_timebank(
    { state, commit, dispatch, getters, rootGetters },
    { position = '', timeout_str = '7_sec' },
  ) {
    // if (timebanksResolved[state.trainer_namespace] >= 9) return
    if (timebanksResolved[state.trainer_namespace] >= 999) return
    if (
      router.currentRoute.value.name !== 'PracticeTrainer' ||
      rootGetters.stats_session_result_visible ||
      rootGetters.panel_replay
    ) {
      return
    }

    const seconds = Number(timeout_str.substring(0, timeout_str.indexOf('_')))
    const conf = {
      seconds,
      position,
      datenow: Date.now(),
    }

    const pl = {
      position,
      timebank: conf,
    }
    commit('player_animation', pl)
    commit('trainer_set', { key: 'trainer_timebank', value: conf })

    state.trainer_timebank_timeout = setTimeout(() => {
      dispatch('trainer_resolve_timebank')
    }, seconds * 1000)
  },
  trainer_clear_timebank({ state, commit, dispatch, getters }) {
    clearTimeout(state.trainer_timebank_timeout)
    state.trainer_timebank_timeout = null

    const conf = state.trainer_timebank
    if (conf) {
      const pl = {
        position: conf.position,
        timebank: null,
      }
      commit('player_animation', pl)
      commit('trainer_set', { key: 'trainer_timebank', value: null })
    }
  },
  trainer_resolve_timebank({ state, commit, dispatch, getters }) {
    timebanksResolved[state.trainer_namespace] = timebanksResolved[state.trainer_namespace]
      ? timebanksResolved[state.trainer_namespace] + 1
      : 1

    let action = null
    const { available_actions } = getters
    action = available_actions.find(a => a.code === 'X' || a.code === 'F')
    dispatch('trainer_handle_action_selection', { action, byUser: false })
  },
  // -------------
  trainer_restore_hand({ state, commit, dispatch, getters }, { action }) {
    const alpha = 'tra/alpha'
    const secNamespaces = ['tra/beta', 'tra/gamma', 'tra/delta']
    const namespaces = ['tra/alpha', ...secNamespaces]

    dispatch('session_reset', null, { root: true })
    commit(`hands_set`, { key: 'locked_namespaces', value: [] }, { root: true })
    dispatch('hands_stop', { namespaces: secNamespaces, configure: false }, { root: true })

    dispatch(`hands_set_active_namespace`, { namespace: 'tra/alpha' }, { root: true })

    dispatch(
      'poker_copy_module',
      {
        from_namespace: 'tra/replay',
        to_namespace: alpha,
        queryGroup: 'replay',
      },
      { root: true },
    )

    dispatch(
      'poker_copy_module',
      {
        from_namespace: alpha,
        to_namespace: alpha,
        queryGroup: 'router-to-gmf-selektor',
      },
      { root: true },
    )

    namespaces.forEach(nn => {
      dispatch(
        `${nn}/router_update_query`,
        {
          query: {
            fh_trainer_tables: '', // exception never copy multiple table settings- rest of the settings should be copied
            fh_trainer_session: '',
          },
          push: false,
        },
        { root: true },
      )
      dispatch(`${nn}/trainer_auto_new_hand_stop`, null, { root: true })
      dispatch(`${nn}/trainer_clear_timebank`, null, { root: true })
    })
    dispatch(`drills_setCheck_selected_drill`, { namespace: alpha }, { root: true })

    if (router.currentRoute.value.name !== 'PracticeTrainer') {
      commit(`hands_set`, { key: 'configure', value: false }, { root: true })
      router.push({ name: 'PracticeTrainer' })
    }

    if (action === 'newhand') {
      dispatch(`${alpha}/trainer_new_hand`, null, { root: true })
    }
    if (action === 'repeathand') {
      dispatch(`${alpha}/trainer_repeat_hand`, null, { root: true })
    }
    if (action === 'switchtocustomspot') {
      dispatch(`${alpha}/trainer_switch_to_custom_spot`, null, { root: true })
    }
    if (action === 'returntogame') {
      dispatch(`${alpha}/trainer_hand_return_to_game`, null, { root: true })
    }

    commit(`panels_set`, { key: 'panel_replay', value: false }, { root: true })
  },

  // --------- action selection
  trainer_handle_action_selection({ state, commit, dispatch, getters }, { action, byUser = true }) {
    if (state.trainer_result_visible) return
    if (byUser) {
      timebanksResolved[state.trainer_namespace] = 0
    }
    dispatch('trainer_clear_timebank')

    const { bot_spot } = getters
    const qqfh = getters.query_fullhand_setup
    const stopAfterAction = qqfh.fh_trainer_mode === 'stop_after_action'
    const resultAfterAction = qqfh.fh_trainer_result === 'after_action'
    const resultAfterMistake = qqfh.fh_trainer_result === 'after_mistake'
    if (stopAfterAction) {
      dispatch('trainer_confirm_action_selection', { action })
      return
    }

    if (!bot_spot) {
      if (resultAfterAction) {
        dispatch('trainer_show_action_result', { action })
        return
      }
      if (
        resultAfterMistake &&
        (action.correctness === 'blunder' || action.correctness === 'wrong_move')
      ) {
        dispatch('trainer_show_action_result', { action })
        return
      }
    }
    dispatch('trainer_confirm_action_selection', { action })
  },
  trainer_show_action_result({ state, commit, dispatch, getters }, { action }) {
    dispatch('trainer_preselect_action', { action })
    commit('trainer_set', { key: 'trainer_result_visible', value: true })
  },
  trainer_preselect_action({ state, commit, dispatch, getters }, { action }) {
    // if (!state.trainer_live) {
    //   commit('trainer_set', { key: 'trainer_live', value: true })
    // }
    const { history_spots, history_spot } = getters
    const hSpots = updateHsSpotActions({
      history_spots,
      spot: history_spot,
      slider_actions: state.trainer_slider_actions,
      action,
      key: 'is_preselected',
      getNext: false,
    })
    commit('gamestate_set', {
      key: 'history_spots',
      value: hSpots,
    })
  },

  trainer_confirm_action_selection({ state, commit, dispatch, getters }, { action }) {
    // if (!state.trainer_live) {
    //   commit('trainer_set', { key: 'trainer_live', value: true })
    // }
    const namespace = state.trainer_namespace
    const { history_spots, history_spot, bot_spot } = getters

    const qqfh = getters.query_fullhand_setup
    const stopAfterAction = qqfh.fh_trainer_mode === 'stop_after_action'
    const stopEndOfStreet = qqfh.fh_trainer_mode === 'stop_end_of_street'

    let hSpots = history_spots.filter(e => e.id <= history_spot.id)
    hSpots = unsetHsSelectedAction({ history_spots: hSpots, spot: history_spot })

    const slider_actions = state.trainer_slider_actions
    hSpots = updateHsSpotActions({
      history_spots: hSpots,
      spot: history_spot,
      action,
      slider_actions,
      key: 'is_selected',
      getNext: true,
    })

    commit('trainer_set', { key: 'trainer_slider_actions', value: null })
    commit('gamestate_set', {
      key: 'history_spots',
      value: hSpots,
    })

    const waitForActionTrack = !bot_spot
    if (waitForActionTrack) {
      dispatch('gamestate_track_action', { namespace, action })
    }

    if (!bot_spot) {
      // trainer_end_spot
      dispatch('stats_add_move', { action, namespace }, { root: true })
      dispatch('trainer_register_cheats')

      if (stopAfterAction) {
        dispatch('streak_set', { correctness_group: action.correctness_group }, { root: true })
        const resultAfterAction = qqfh.fh_trainer_result === 'after_action'
        const resultAfterMistake = qqfh.fh_trainer_result === 'after_mistake'

        let continueNextHand = true
        if (resultAfterAction) {
          continueNextHand = false
        }
        if (
          resultAfterMistake &&
          (action.correctness === 'blunder' || action.correctness === 'wrong_move')
        ) {
          continueNextHand = false
        }

        dispatch('trainer_show_quick_result', { action })
        dispatch('api_practice_register_showdown', { namespace }, { root: true })
        dispatch('trainer_hand_finish', { continueNextHand })
        return
      }
    }

    dispatch('trainer_show_quick_result', { action })

    const aaOutcome = action.outcome
    if (action.is_fe_solution_end) {
      hSpots[hSpots.length - 1].selected_action_outcome = 'showdown'
      commit('gamestate_set', { key: 'history_spots', value: hSpots })
      dispatch('gamestate_get_api_practice_showdown', { namespace, animate: true })
      return
    }
    if (stopEndOfStreet && aaOutcome === 'board') {
      hSpots[hSpots.length - 1].selected_action_outcome = 'showdown'
      commit('gamestate_set', { key: 'history_spots', value: hSpots })
      dispatch('gamestate_get_api_practice_showdown', { namespace, animate: true })
      return
    }
    if (aaOutcome === 'showdown') {
      dispatch('gamestate_get_api_practice_showdown', { namespace, animate: action.next_street })
      return
    }

    const hero_position = qqfh.fh_hero_tmp
    if (hero_position === action.next_position) {
      dispatch('gamestate_get_hero_actions', {
        namespace,
        waitForActionTrack,
      })
    } else {
      dispatch('gamestate_get_bot_action', {
        namespace,
        position: action.next_position,
        waitForActionTrack,
      })
      commit('trainer_set', { key: 'trainer_result_visible', value: false })
    }
  },

  trainer_select_quizz_action({ state, commit, dispatch, getters }, { answer }) {
    const namespace = state.trainer_namespace
    dispatch('api_practice_quizz_tracking', { namespace, answer }, { root: true })

    let ovrSpot = state.trainer_overview_spot
    ovrSpot = {
      ...ovrSpot,
      challenge: {
        ...ovrSpot.api_practice.challenge,
        variants: ovrSpot.api_practice.challenge.variants.map(e => {
          e.is_selected = e.id === answer.id
          return e
        }),
      },
    }

    const hSpots = getters.history_spots.map(e => {
      if (e.id === ovrSpot.id) {
        e = ovrSpot
      }
      return e
    })

    state.trainer_overview_spot = ovrSpot
    commit('gamestate_set', {
      key: 'history_spots',
      value: hSpots,
    })
  },
}
const mutations = {
  trainer_reset(state, initialState = getInitialState()) {
    Object.assign(state, initialState)
  },
  trainer_set(state, { key, value }) {
    state[key] = value
  },
}

function getTrainerModule(namespace) {
  return {
    state: getInitialState({ namespace }),
    getters,
    actions,
    mutations,
  }
}

export { getTrainerModule }
